import { DigitalWastageWithSegmentation, useDigitalWastage } from '@percept/hooks'

import { get, uniq } from 'lodash-es'

import { coerceReportProvider, getComparisonFunction, produceKeyedMapping } from '@percept/utils'

import { comparisonRangeGenerators } from './lib'

import { PerformanceRangeKey, PlatformUnitParams, ReportProvider, ReportProviderV2 } from '@percept/types'
import { useMemo } from 'react'


const relativeComparison = getComparisonFunction('RELATIVE')

const wastageComparisonValue: 'wastage_cost' | 'wastage_ratio' = 'wastage_ratio'

const getWastageComparison = ({
  benchmarkData,
  comparisonData,
}: {
  benchmarkData: DigitalWastageWithSegmentation[] | undefined,
  comparisonData: DigitalWastageWithSegmentation[] | undefined,
}): Record<ReportProvider, number | null> | null => {
  if( !(benchmarkData && comparisonData) ){
    return null
  }
  const benchmarkByProvider = produceKeyedMapping(benchmarkData, 'segment_value')
  const comparisonByProvider = produceKeyedMapping(comparisonData, 'segment_value')
  const providers = uniq([
    ...Object.keys(benchmarkByProvider) as ReportProviderV2[],
    ...Object.keys(comparisonByProvider) as ReportProviderV2[],
  ])
  return providers.reduce( (acc, provider) => {
    acc[coerceReportProvider(provider as ReportProvider) as ReportProvider] = relativeComparison(
      get(comparisonByProvider, [provider, wastageComparisonValue], null),
      get(benchmarkByProvider, [provider, wastageComparisonValue], null),
    )
    return acc
  }, {} as Record<ReportProvider, number | null>)
}

type ComparisonQuery = {
  data: Record<ReportProvider, number | null> | null
  loading: boolean
}

const useWastagePeriodComparison = ({
  org_unit_id,
  referenceDate,
  performanceRange,
}: PlatformUnitParams & {
  referenceDate: Date | null
  performanceRange: PerformanceRangeKey
}): ComparisonQuery => {
  const [benchmarkRange, comparisonRange] = referenceDate ? comparisonRangeGenerators[performanceRange](referenceDate) : [null, null]
  const benchmarkQuery = useDigitalWastage({
    org_unit_id,
    segment_type: 'PROVIDER',
    start: benchmarkRange && benchmarkRange[0],
    end: benchmarkRange && benchmarkRange[1],
  })
  const comparisonQuery = useDigitalWastage({
    org_unit_id,
    segment_type: 'PROVIDER',
    start: comparisonRange && comparisonRange[0],
    end: comparisonRange && comparisonRange[1],
  })
  return useMemo(() => {
    if( !comparisonQuery.data || !benchmarkQuery.data ){
      return { data: null, loading: true }
    }
    return {
      data: getWastageComparison({
        benchmarkData: benchmarkQuery.data,
        comparisonData: comparisonQuery.data,
      }),
      loading: false,
    }
  }, [comparisonQuery.data, benchmarkQuery.data])
}

export type ComparisonQueryByPeriod = Record<
  PerformanceRangeKey, ComparisonQuery
>

export const useWastageProviderComparisons = ({
  org_unit_id,
  referenceDate,
}: PlatformUnitParams & {
  referenceDate: Date | null
}): ComparisonQueryByPeriod => {
  // const referenceDates = useWastageReferenceDates({ org_unit_id })
  // let referenceDate: Date | null = null
  // if( referenceDates.data ){
  //   referenceDate = referenceDates.data.end
  // }
  // referenceDate = new Date('2025-04-27')
  return {
    week: useWastagePeriodComparison({ org_unit_id, referenceDate, performanceRange: 'week' }),
    month: useWastagePeriodComparison({ org_unit_id, referenceDate, performanceRange: 'month' }),
    quarter: useWastagePeriodComparison({ org_unit_id, referenceDate, performanceRange: 'quarter' }),
    year: useWastagePeriodComparison({ org_unit_id, referenceDate, performanceRange: 'year' }),
    now: { data: null, loading: false },
    half_year: { data: null, loading: false },
  }
}
